import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { haptic } from "../telegram";
import { useBackNavigation } from "../use-back-navigation";

type ReportQueueItem = {
  reportId: string;
  reason: string;
  details: string | null;
  createdAt: string;
  reporterName: string | null;
  target: {
    userId: string;
    status: "active" | "frozen" | "banned" | "deleted";
    displayName: string | null;
    age: number | null;
    city: string | null;
  };
  relatedOpenReports: number;
};

type ReportPage = {
  items: ReportQueueItem[];
  nextCursor: string | null;
};

type ReportDetail = {
  report: {
    id: string;
    reason: string;
    details: string | null;
    status: string;
    resolution: string | null;
    createdAt: string;
    reporter: {
      userId: string;
      displayName: string | null;
    };
    target: {
      userId: string;
      status: "active" | "frozen" | "banned" | "deleted";
      displayName: string | null;
      age: number | null;
      city: string | null;
      createdAt: string;
    };
  };
  recentMessages: Array<{
    id: string;
    senderUserId: string;
    body: string;
    createdAt: string;
  }>;
  recentActions: Array<{
    action: string;
    reason: string;
    metadata: Record<string, unknown>;
    createdAt: string;
  }>;
};

type Decision = "dismiss" | "resolve" | "freeze" | "ban";

const reasonLabels: Record<string, string> = {
  spam: "Спам",
  fraud: "Мошенничество",
  fake_profile: "Фейковая анкета",
  abuse_or_threats: "Оскорбления или угрозы",
  unwanted_sexual_content: "Нежелательный сексуальный контент",
  suspected_minor: "Подозрение на несовершеннолетнего",
  other: "Другое",
};

const actionLabels: Record<string, string> = {
  warning: "Предупреждение",
  restrict: "Ограничение",
  freeze: "Заморозка аккаунта",
  unfreeze: "Снятие заморозки",
  ban: "Блокировка аккаунта",
  unban: "Снятие блокировки",
  photo_approve: "Фото одобрено",
  photo_reject: "Фото отклонено",
  report_resolve: "Жалоба подтверждена",
  report_dismiss: "Жалоба отклонена",
};

export function AdminReports({ role, onChanged }: { role: string; onChanged(): Promise<void> }) {
  const [error, setError] = useState("");
  const [items, setItems] = useState<ReportQueueItem[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReportDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [note, setNote] = useState("");
  const [confirmDecision, setConfirmDecision] = useState<"freeze" | "ban" | null>(null);
  const canBan = role === "admin" || role === "super_admin";

  const loadQueue = useCallback(async () => {
    const result = await api<ReportPage>("/v1/admin/moderation/reports?limit=20");
    setItems(result.items);
    setNextCursor(result.nextCursor);
  }, []);

  useEffect(() => {
    void loadQueue().catch(() => {
      setItems([]);
      setError("Не удалось загрузить список жалоб.");
    });
  }, [loadQueue]);

  const openReport = async (reportId: string) => {
    setBusy(true);
    setError("");
    try {
      const detail = await api<ReportDetail>(`/v1/admin/moderation/reports/${reportId}`);
      setSelected(detail);
      setNote("");
      setConfirmDecision(null);
    } catch {
      setError("Не удалось выполнить действие. Данные сохранены на экране; повторите попытку.");
    } finally {
      setBusy(false);
    }
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError("");
    try {
      const result = await api<ReportPage>(
        `/v1/admin/moderation/reports?limit=20&cursor=${encodeURIComponent(nextCursor)}`,
      );
      setItems((current) => [...(current ?? []), ...result.items]);
      setNextCursor(result.nextCursor);
    } catch {
      setError("Не удалось загрузить следующую страницу.");
    } finally {
      setLoadingMore(false);
    }
  };

  const decide = async (decision: Decision) => {
    if (!selected || busy) return;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/admin/moderation/reports/${selected.report.id}/decision`, {
        method: "POST",
        body: JSON.stringify({
          decision,
          ...(note.trim() ? { note: note.trim() } : {}),
        }),
      });
      haptic(decision === "dismiss" ? "pass" : "success");
      setItems((items) => items?.filter((item) => item.reportId !== selected.report.id) ?? null);
      setSelected(null);
      setConfirmDecision(null);
      setNote("");
      await Promise.all([loadQueue(), onChanged()]).catch(() =>
        setError("Решение сохранено. Не удалось обновить очередь."),
      );
    } catch {
      setError("Не удалось выполнить действие. Данные сохранены на экране; повторите попытку.");
    } finally {
      setBusy(false);
    }
  };

  useBackNavigation(() => {
    if (busy) return;
    if (confirmDecision) setConfirmDecision(null);
    else setSelected(null);
  }, Boolean(selected));
  const refresh = async () => {
    setError("");
    try {
      await loadQueue();
    } catch {
      setError("Не удалось обновить список жалоб.");
    }
  };
  const feedback = error && (
    <div className="reference-error" role="alert">
      {error}
      <button type="button" className="secondary-button" disabled={busy || loadingMore} onClick={() => void refresh()}>
        Повторить загрузку
      </button>
    </div>
  );
  if (error && !selected && !items?.length) return <section className="admin-load-error">{feedback}</section>;
  if (!items) return <div className="list-skeleton" />;

  if (selected) {
    const targetName = selected.report.target.displayName ?? "Профиль без имени";
    return (
      <section className="report-detail">
        <button type="button" className="admin-back-button" disabled={busy} onClick={() => setSelected(null)}>
          ← К очереди
        </button>

        <div className="report-detail-heading">
          <div>
            <span className="eyebrow">{reasonLabels[selected.report.reason] ?? selected.report.reason}</span>
            <h2>{targetName}</h2>
            <p>
              {selected.report.target.age ? `${selected.report.target.age} · ` : ""}
              {selected.report.target.city ?? "Город не указан"}
            </p>
          </div>
          <span className={`account-status ${selected.report.target.status}`}>{selected.report.target.status}</span>
        </div>

        <div className="report-facts">
          <div>
            <span>Автор жалобы</span>
            <strong>{selected.report.reporter.displayName ?? "Пользователь"}</strong>
          </div>
          <div>
            <span>Дата</span>
            <strong>{new Date(selected.report.createdAt).toLocaleString("ru-RU")}</strong>
          </div>
        </div>

        <article className="report-copy">
          <span>Описание жалобы</span>
          <p>{selected.report.details || "Пользователь не добавил комментарий."}</p>
        </article>

        <section className="moderation-context">
          <div className="moderation-section-title">
            <h3>Последние сообщения</h3>
            <span>{selected.recentMessages.length}</span>
          </div>
          {selected.recentMessages.length === 0 ? (
            <p className="admin-muted">Переписка между этими пользователями не найдена.</p>
          ) : (
            <div className="moderation-message-list">
              {selected.recentMessages.map((message) => {
                const fromReporter = message.senderUserId === selected.report.reporter.userId;
                return (
                  <div
                    className={fromReporter ? "moderation-message reporter" : "moderation-message target"}
                    key={message.id}
                  >
                    <span>{fromReporter ? "Автор жалобы" : targetName}</span>
                    <p>{message.body}</p>
                    <time>{new Date(message.createdAt).toLocaleString("ru-RU")}</time>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="moderation-context">
          <div className="moderation-section-title">
            <h3>История модерации</h3>
            <span>{selected.recentActions.length}</span>
          </div>
          {selected.recentActions.length === 0 ? (
            <p className="admin-muted">Предыдущих действий нет.</p>
          ) : (
            <div className="moderation-history">
              {selected.recentActions.map((action) => (
                <div key={`${action.action}-${action.createdAt}`}>
                  <strong>{actionLabels[action.action] ?? action.action}</strong>
                  <span>{action.reason}</span>
                  <time>{new Date(action.createdAt).toLocaleString("ru-RU")}</time>
                </div>
              ))}
            </div>
          )}
        </section>

        <label className="moderator-note">
          <span>
            Внутренняя заметка <em>необязательно</em>
          </span>
          <textarea
            maxLength={500}
            value={note}
            placeholder="Контекст решения для audit trail"
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        {feedback}
        {confirmDecision ? (
          <div className="danger-confirmation">
            <strong>{confirmDecision === "ban" ? "Заблокировать аккаунт?" : "Заморозить аккаунт?"}</strong>
            <p>
              {confirmDecision === "ban"
                ? "Постоянная блокировка завершит активные сессии и закроет все открытые жалобы на пользователя."
                : "Пользователь сразу потеряет доступ к аккаунту до отдельного решения администратора."}
            </p>
            <div className="admin-actions">
              <button
                type="button"
                className="secondary-button"
                disabled={busy}
                onClick={() => setConfirmDecision(null)}
              >
                Отмена
              </button>
              <button type="button" className="danger-button" disabled={busy} onClick={() => decide(confirmDecision)}>
                {busy ? "Сохраняем…" : "Подтвердить"}
              </button>
            </div>
          </div>
        ) : (
          <div className="report-actions">
            <button type="button" className="secondary-button" disabled={busy} onClick={() => decide("dismiss")}>
              Отклонить жалобу
            </button>
            <button type="button" className="secondary-button" disabled={busy} onClick={() => decide("resolve")}>
              Подтвердить без санкции
            </button>
            <button
              type="button"
              className="warning-button"
              disabled={busy}
              onClick={() => setConfirmDecision("freeze")}
            >
              Заморозить
            </button>
            {canBan && (
              <button type="button" className="danger-button" disabled={busy} onClick={() => setConfirmDecision("ban")}>
                Заблокировать
              </button>
            )}
          </div>
        )}
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="empty-state admin-empty">
        <div className="empty-orbit">✓</div>
        <h2>Жалоб нет</h2>
        <p>Открытые пользовательские жалобы появятся здесь.</p>
        <button type="button" className="secondary-button" onClick={() => void refresh()}>
          Обновить
        </button>
      </section>
    );
  }

  return (
    <section className="report-queue">
      {feedback}
      {items.map((item) => (
        <button
          type="button"
          className="report-row"
          key={item.reportId}
          disabled={busy}
          onClick={() => openReport(item.reportId)}
        >
          <span className="report-reason">{reasonLabels[item.reason] ?? item.reason}</span>
          <strong>
            {item.target.displayName ?? "Профиль без имени"}
            {item.target.age ? `, ${item.target.age}` : ""}
          </strong>
          <span className="report-summary">
            {item.details || "Без комментария"} · {item.target.city ?? "город не указан"}
          </span>
          <span className="report-row-footer">
            <span>{new Date(item.createdAt).toLocaleString("ru-RU")}</span>
            {item.relatedOpenReports > 1 && <b>Жалоб: {item.relatedOpenReports}</b>}
          </span>
        </button>
      ))}
      {nextCursor && (
        <button type="button" className="load-more-conversations" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? "Загружаем…" : "Показать ещё"}
        </button>
      )}
    </section>
  );
}
